import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/client';
import { Link, useSearchParams } from 'react-router-dom';
import PeriodToolbar from '../components/PeriodToolbar';
import { usePeriod } from '../context/PeriodContext';
import { SkeletonCard } from '../components/Skeleton';
import { ErrorState } from '../components/ErrorState';
import type { ApiError } from '../api/client';
import HubFinanceTile from '../components/HubFinanceTile';
import PerformanceSummaryCard from '../components/PerformanceSummaryCard';
import { saveBlob, slugify } from '../api/download';

type DirectReport = {
  upn: string;
  displayName: string;
};

type TeamRow = {
  AssignmentId: number;
  EmployeeUpn: string;
  DisplayName: string;
  KpiCode: string;
  KpiTitle: string;
  IsRecurring?: boolean;
  PeriodId: number;
  PeriodName: string;
  PeriodStart: string;
  PeriodEnd: string;
  HasSelfRating: number;
  HasManagerRating: number;
  SelfScore: number | null;
  ManagerScore: number | null;
  Weight: number;
};

type EmployeeSummary = {
  upn: string;
  displayName: string;
  totalKpis: number;
  selfRated: number;
  managerRated: number;
  avgSelfScore: number | null;
  avgManagerScore: number | null;
  weightTotal: number;
  completionPct: number;
  needsAttention: boolean;
  fiscalYears: string[]; // e.g. ["FY25", "FY26"]
  periodRange: string;   // e.g. "Dec 24 - Nov 25"
};

export const TeamAssignmentsPage = () => {
  const api = useApi();
  const { dueDate } = usePeriod();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const employeeFromUrl = searchParams.get('employee');
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(employeeFromUrl);
  // Keep the person in the URL so a fresh mount (Back, or return after the last KPI) restores them.
  const selectEmployee = (upn: string | null) => {
    setSelectedEmployee(upn);
    const next = new URLSearchParams(searchParams);
    if (upn) next.set('employee', upn);
    else next.delete('employee');
    setSearchParams(next, { replace: true });
  };
  useEffect(() => {
    if (employeeFromUrl) setSelectedEmployee(employeeFromUrl);
  }, [employeeFromUrl]);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const showToast = (msg: string) => { setToastMsg(msg); setTimeout(() => setToastMsg(null), 2500); };
  
  // Date filter mode: fiscalYear, dueDate, or allTime
  const [dateFilterMode, setDateFilterMode] = useState<'fiscalYear' | 'dueDate' | 'allTime'>('fiscalYear');
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth(); // 0-11
  // Default fiscal year: if we're in Dec, it's next FY; otherwise current year
  const defaultFY = currentMonth === 11 ? currentYear + 1 : currentYear;
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<number>(defaultFY);
  
  // Generate fiscal year options (last 3 years + next year)
  const fiscalYearOptions = [
    currentYear + 1,
    currentYear,
    currentYear - 1,
    currentYear - 2,
  ].filter((y, i, arr) => arr.indexOf(y) === i);
  
  // Calculate date range based on filter mode
  const getDateRange = () => {
    if (dateFilterMode === 'allTime') {
      return { startDate: '2020-01-01', endDate: '2099-12-31' };
    }
    if (dateFilterMode === 'fiscalYear') {
      // FY runs Dec (year-1) through Nov (year)
      // e.g., FY26 = Dec 2025 through Nov 2026
      const startDate = `${selectedFiscalYear - 1}-12-01`;
      const endDate = `${selectedFiscalYear}-11-30`;
      return { startDate, endDate };
    }
    // dueDate mode uses the period context
    return { startDate: undefined, endDate: dueDate };
  };
  
  const { startDate, endDate } = getDateRange();

  // Get direct reports list
  const { data: directsData, isLoading: directsLoading } = useQuery({
    queryKey: ['directs'],
    queryFn: () => api.get('/employees/directs') as Promise<DirectReport[]>,
  });
  const directs: DirectReport[] = directsData ?? [];

  // Get team assignments
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['assignments-team', dateFilterMode, selectedFiscalYear, dueDate],
    enabled: Boolean(endDate),
    retry: 2,
    staleTime: 30000,
    queryFn: async () => {
      let url = `/assignments/team?endDate=${endDate}`;
      if (startDate) {
        url += `&startDate=${startDate}`;
      }
      return await api.get(url);
    }
  });

  const rows: TeamRow[] = data?.items ?? [];

  const recurringMutation = useMutation({
    mutationFn: (vars: { kpiCode: string; is_recurring: boolean }) =>
      api.put(`/kpis/by-code/${encodeURIComponent(vars.kpiCode)}`, { is_recurring: vars.is_recurring }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ['assignments-team'] });
      const previous = qc.getQueriesData({ queryKey: ['assignments-team'] });
      qc.setQueriesData({ queryKey: ['assignments-team'] }, (old: unknown) => {
        const data = old as { items?: TeamRow[] } | undefined;
        if (!data?.items || !Array.isArray(data.items)) return old;
        return {
          ...data,
          items: data.items.map((row) =>
            row.KpiCode === vars.kpiCode ? { ...row, IsRecurring: vars.is_recurring } : row
          ),
        };
      });
      return { previous };
    },
    onError: (err: any, _vars, ctx) => {
      if (ctx?.previous) {
        for (const [key, data] of ctx.previous) qc.setQueryData(key, data);
      }
      showToast(`Recurring update failed: ${err?.body?.detail || err?.message || 'Unknown error'}`);
    },
    onSuccess: (_data, vars) => {
      showToast(vars.is_recurring ? 'Recurring on ✓' : 'Recurring off ✓');
    },
  });

  // Group by employee and calculate summaries
  const employeeSummaries: EmployeeSummary[] = useMemo(() => {
    const grouped = new Map<string, TeamRow[]>();
    
    // Build a set of valid direct report UPNs for quick lookup
    const directUpns = new Set(directs.map(d => d.upn));
    
    // Initialize from directs list (even if they have no assignments)
    directs.forEach(d => {
      grouped.set(d.upn, []);
    });
    
    // Add actual assignments - ONLY for employees in the directs list
    rows.forEach(row => {
      if (directUpns.has(row.EmployeeUpn)) {
        const existing = grouped.get(row.EmployeeUpn) ?? [];
        existing.push(row);
        grouped.set(row.EmployeeUpn, existing);
      }
    });

    return Array.from(grouped.entries()).map(([upn, kpis]) => {
      const direct = directs.find(d => d.upn === upn);
      const totalKpis = kpis.length;
      const selfRated = kpis.filter(k => k.HasSelfRating).length;
      const managerRated = kpis.filter(k => k.HasManagerRating).length;
      
      const selfScores = kpis.filter(k => k.SelfScore !== null).map(k => k.SelfScore!);
      const mgrScores = kpis.filter(k => k.ManagerScore !== null).map(k => k.ManagerScore!);
      
      const avgSelfScore = selfScores.length > 0 
        ? selfScores.reduce((a, b) => a + b, 0) / selfScores.length 
        : null;
      const avgManagerScore = mgrScores.length > 0 
        ? mgrScores.reduce((a, b) => a + b, 0) / mgrScores.length 
        : null;
      
      const weightTotal = kpis.reduce((acc, k) => acc + (k.Weight ?? 0), 0);
      
      const completionPct = totalKpis > 0 ? Math.round((managerRated / totalKpis) * 100) : 0;
      const needsAttention = totalKpis > 0 && (selfRated < totalKpis || managerRated < totalKpis);

      // Calculate fiscal years from periods (Dec-Nov cycle, Dec belongs to next FY)
      const fiscalYearSet = new Set<string>();
      let minDate: Date | null = null;
      let maxDate: Date | null = null;
      
      kpis.forEach(k => {
        if (k.PeriodStart) {
          const d = new Date(k.PeriodStart);
          const month = d.getMonth(); // 0=Jan, 11=Dec
          const year = d.getFullYear();
          // Dec (month 11) belongs to next FY, Jan-Nov belong to current FY
          const fiscalYear = month === 11 ? year + 1 : year;
          fiscalYearSet.add(`FY${String(fiscalYear).slice(-2)}`);
          
          if (!minDate || d < minDate) minDate = d;
          if (!maxDate || d > maxDate) maxDate = d;
        }
      });
      
      const fiscalYears = Array.from(fiscalYearSet).sort();
      
      // Format period range
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const periodRange = minDate && maxDate 
        ? `${monthNames[minDate.getMonth()]} ${String(minDate.getFullYear()).slice(-2)} - ${monthNames[maxDate.getMonth()]} ${String(maxDate.getFullYear()).slice(-2)}`
        : '';

      return {
        upn,
        displayName: direct?.displayName || kpis[0]?.DisplayName || upn,
        totalKpis,
        selfRated,
        managerRated,
        avgSelfScore,
        avgManagerScore,
        weightTotal,
        completionPct,
        needsAttention,
        fiscalYears,
        periodRange,
      };
    }).sort((a, b) => {
      // Sort: needs attention first, then by name
      if (a.needsAttention !== b.needsAttention) return a.needsAttention ? -1 : 1;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [rows, directs]);

  // Team totals
  const teamTotals = useMemo(() => {
    const total = rows.length;
    const selfDone = rows.filter(r => r.HasSelfRating).length;
    const mgrDone = rows.filter(r => r.HasManagerRating).length;
    const needsYourReview = rows.filter(r => r.HasSelfRating && !r.HasManagerRating).length;
    return { total, selfDone, mgrDone, needsYourReview, teamMembers: employeeSummaries.length };
  }, [rows, employeeSummaries]);

  // Selected employee's KPIs (latest period first)
  const selectedKpis = selectedEmployee
    ? rows.filter(r => r.EmployeeUpn === selectedEmployee)
        .slice()
        .sort((a, b) => {
          const ae = a.PeriodEnd || a.PeriodStart || a.PeriodName || '';
          const be = b.PeriodEnd || b.PeriodStart || b.PeriodName || '';
          return be.localeCompare(ae);
        })
    : [];

  const isPageLoading = isLoading || directsLoading;

  if (isPageLoading) {
    return (
      <div>
        <h2>Team Overview</h2>
        <div style={{ padding: '12px 16px', background: '#f9fafb', borderRadius: '8px', marginBottom: '1rem' }}>
          <span style={{ fontSize: '13px', color: '#6b7280' }}>Loading filters...</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
          <SkeletonCard count={6} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h2>Team Overview</h2>
        <ErrorState
          title="Failed to Load Team Data"
          message={(error as ApiError)?.body?.detail || 'Unable to load team data. Please try again.'}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  /** Excel export of the current Team view: date filter (FY / due date / all time) + selected person. */
  const downloadTeamKpis = async () => {
    const params = new URLSearchParams({ scope: 'team', format: 'xlsx' });
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (selectedEmployee) params.set('employeeUpn', selectedEmployee);
    setDownloading(true);
    try {
      const blob = await api.getBlob(`/assignments/export?${params.toString()}`);
      const who = selectedEmployee
        ? slugify(rows.find(r => r.EmployeeUpn.toLowerCase() === selectedEmployee.toLowerCase())?.DisplayName || selectedEmployee.split('@')[0])
        : 'team';
      const range = dateFilterMode === 'fiscalYear' ? `-fy${selectedFiscalYear}` : dateFilterMode === 'allTime' ? '-all' : '';
      saveBlob(blob as Blob, `kpis-${who}${range}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err: any) {
      showToast(`Download failed: ${err?.body?.detail || err?.message || 'unknown error'}`);
    } finally {
      setDownloading(false);
    }
  };


  if (employeeSummaries.length === 0) {
    return (
      <div>
        <h2>Team Overview</h2>
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>👥</div>
          <h3 style={{ margin: '0 0 8px', color: '#374151' }}>No Team Members</h3>
          <p>No direct reports found for this account.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {toastMsg && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, background: toastMsg.includes('failed') ? '#dc2626' : '#065f46', color: '#fff', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {toastMsg}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ margin: 0 }}>Team Overview</h2>
          <button
            className={`refresh-btn${isFetching ? ' spinning' : ''}`}
            onClick={() => refetch()}
            title="Refresh data"
            disabled={isFetching}
          >↻</button>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={downloadTeamKpis}
            disabled={downloading}
            style={{
              padding: '6px 12px', fontSize: 13, borderRadius: 6,
              border: '1px solid #d1d5db', background: '#fff', color: '#374151',
              cursor: 'pointer', fontWeight: 600,
            }}
            title={selectedEmployee ? 'Download this person\'s KPIs for the selected dates as an Excel workbook' : 'Download team KPIs for the selected dates as an Excel workbook (Dashboard + KPI detail)'}
          >
            {downloading ? 'Preparing…' : '⬇ Download KPIs'}
          </button>
          <button
            onClick={() => setViewMode('cards')}
            style={{
              padding: '6px 12px', fontSize: 13, borderRadius: 6,
              border: viewMode === 'cards' ? '2px solid #3b82f6' : '1px solid #d1d5db',
              background: viewMode === 'cards' ? '#eff6ff' : '#fff',
              color: '#374151',
              cursor: 'pointer',
            }}
          >
            📊 Cards
          </button>
          <button
            onClick={() => setViewMode('table')}
            style={{
              padding: '6px 12px', fontSize: 13, borderRadius: 6,
              border: viewMode === 'table' ? '2px solid #3b82f6' : '1px solid #d1d5db',
              background: viewMode === 'table' ? '#eff6ff' : '#fff',
              color: '#374151',
              cursor: 'pointer',
            }}
          >
            📋 Table
          </button>
        </div>
      </div>
      
      {/* Date Filter Toolbar */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '1rem', 
        marginBottom: '1rem',
        padding: '12px 16px',
        background: '#f9fafb',
        borderRadius: '8px',
        flexWrap: 'wrap'
      }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>View by:</span>
        
        {/* Filter Mode Buttons */}
        <div style={{ display: 'flex', gap: '4px' }}>
          <button
            onClick={() => setDateFilterMode('fiscalYear')}
            style={{
              padding: '6px 14px', fontSize: 13, borderRadius: 6,
              border: dateFilterMode === 'fiscalYear' ? '2px solid #3b82f6' : '1px solid #d1d5db',
              background: dateFilterMode === 'fiscalYear' ? '#eff6ff' : '#fff',
              color: '#374151',
              fontWeight: dateFilterMode === 'fiscalYear' ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            📅 Financial Year
          </button>
          <button
            onClick={() => setDateFilterMode('dueDate')}
            style={{
              padding: '6px 14px', fontSize: 13, borderRadius: 6,
              border: dateFilterMode === 'dueDate' ? '2px solid #3b82f6' : '1px solid #d1d5db',
              background: dateFilterMode === 'dueDate' ? '#eff6ff' : '#fff',
              color: '#374151',
              fontWeight: dateFilterMode === 'dueDate' ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            ⏰ Due Date
          </button>
          <button
            onClick={() => setDateFilterMode('allTime')}
            style={{
              padding: '6px 14px', fontSize: 13, borderRadius: 6,
              border: dateFilterMode === 'allTime' ? '2px solid #3b82f6' : '1px solid #d1d5db',
              background: dateFilterMode === 'allTime' ? '#eff6ff' : '#fff',
              color: '#374151',
              fontWeight: dateFilterMode === 'allTime' ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            ∞ All Time
          </button>
        </div>
        
        {/* Fiscal Year Selector */}
        {dateFilterMode === 'fiscalYear' && (
          <select
            value={selectedFiscalYear}
            onChange={(e) => setSelectedFiscalYear(Number(e.target.value))}
            style={{
              padding: '6px 12px', fontSize: 13, borderRadius: 6,
              border: '1px solid #d1d5db', background: '#fff',
              cursor: 'pointer', fontWeight: 500,
            }}
          >
            {fiscalYearOptions.map(fy => (
              <option key={fy} value={fy}>
                FY{String(fy).slice(-2)} (Dec {fy - 1} - Nov {fy})
              </option>
            ))}
          </select>
        )}
        
        {/* Show PeriodToolbar inline for Due Date mode */}
        {dateFilterMode === 'dueDate' && (
          <div style={{ flex: 1 }}>
            <PeriodToolbar />
          </div>
        )}
        
        {/* Current filter description */}
        <span style={{ 
          fontSize: '12px', 
          color: '#6b7280',
          marginLeft: 'auto',
          padding: '4px 10px',
          background: '#e5e7eb',
          borderRadius: '4px'
        }}>
          {dateFilterMode === 'fiscalYear' && `Showing: Dec ${selectedFiscalYear - 1} – Nov ${selectedFiscalYear}`}
          {dateFilterMode === 'dueDate' && `Showing: Up to ${dueDate}`}
          {dateFilterMode === 'allTime' && `Showing: All historical data`}
        </span>
      </div>

      {/* Team Summary Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(5, 1fr)', 
        gap: '1rem', 
        marginBottom: '2rem',
        marginTop: '1rem'
      }}>
        <StatCard label="Team Members" value={teamTotals.teamMembers} icon="👥" />
        <StatCard label="Total KPIs" value={teamTotals.total} icon="📋" />
        <StatCard label="Self Rated" value={`${teamTotals.selfDone}/${teamTotals.total}`} icon="✍️" />
        <StatCard label="Manager Reviewed" value={`${teamTotals.mgrDone}/${teamTotals.total}`} icon="✅" />
        <StatCard 
          label="Needs Your Review" 
          value={teamTotals.needsYourReview} 
          icon="⚠️" 
          highlight={teamTotals.needsYourReview > 0}
        />
      </div>

      {viewMode === 'cards' ? (
        /* Card View */
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
          gap: '1rem' 
        }}>
          {employeeSummaries.map(emp => (
            <TeamMemberCard 
              key={emp.upn} 
              employee={emp}
              isSelected={selectedEmployee === emp.upn}
              onSelect={() => selectEmployee(selectedEmployee === emp.upn ? null : emp.upn)}
            />
          ))}
        </div>
      ) : (
        /* Table View */
        <table style={{ width: '100%', fontSize: '14px' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '12px 8px' }}>Employee</th>
              <th style={{ textAlign: 'center', padding: '12px 8px' }}>KPIs</th>
              <th style={{ textAlign: 'center', padding: '12px 8px' }}>Self Rated</th>
              <th style={{ textAlign: 'center', padding: '12px 8px' }}>Manager Rated</th>
              <th style={{ textAlign: 'center', padding: '12px 8px' }}>Avg Score</th>
              <th style={{ textAlign: 'center', padding: '12px 8px' }}>Progress</th>
              <th style={{ textAlign: 'center', padding: '12px 8px' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {employeeSummaries.map(emp => (
              <tr key={emp.upn} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '12px 8px', fontWeight: 600 }}>{emp.displayName}</td>
                <td style={{ textAlign: 'center', padding: '12px 8px' }}>{emp.totalKpis}</td>
                <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                  <span style={{ 
                    color: emp.selfRated === emp.totalKpis ? '#059669' : '#f59e0b' 
                  }}>
                    {emp.selfRated}/{emp.totalKpis}
                  </span>
                </td>
                <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                  <span style={{ 
                    color: emp.managerRated === emp.totalKpis ? '#059669' : '#f59e0b' 
                  }}>
                    {emp.managerRated}/{emp.totalKpis}
                  </span>
                </td>
                <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                  {emp.avgManagerScore !== null ? emp.avgManagerScore.toFixed(1) : '—'}
                </td>
                <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                  <ProgressBar pct={emp.completionPct} />
                </td>
                <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                  <button
                    onClick={() => selectEmployee(selectedEmployee === emp.upn ? null : emp.upn)}
                    style={{
                      padding: '4px 10px', fontSize: 12, borderRadius: 4,
                      border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer'
                    }}
                  >
                    {selectedEmployee === emp.upn ? 'Hide' : 'View KPIs'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedEmployee && (
        <>
          <PerformanceSummaryCard
            employeeUpn={selectedEmployee}
            employeeName={employeeSummaries.find(e => e.upn === selectedEmployee)?.displayName}
            periodId={selectedKpis[0]?.PeriodId ?? null}
            compact
          />
          <HubFinanceTile
            employeeUpn={selectedEmployee}
            periodName={selectedKpis[0]?.PeriodName}
          />
        </>
      )}

      {/* Selected Employee KPI Details */}
      {selectedEmployee && selectedKpis.length > 0 && (
        <div style={{ 
          marginTop: '2rem', 
          padding: '1.5rem', 
          background: '#f9fafb', 
          borderRadius: '12px',
          border: '2px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>
              {employeeSummaries.find(e => e.upn === selectedEmployee)?.displayName}'s KPIs
            </h3>
            <button
              onClick={() => selectEmployee(null)}
              style={{
                padding: '6px 12px', fontSize: 13, borderRadius: 6,
                border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer'
              }}
            >
              ✕ Close
            </button>
          </div>
          <table style={{ width: '100%', fontSize: '14px', background: '#fff', borderRadius: '8px' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ textAlign: 'left', padding: '10px 12px' }}>KPI</th>
                <th style={{ textAlign: 'center', padding: '10px 12px' }}>Period</th>
                <th style={{ textAlign: 'center', padding: '10px 12px' }}>Self</th>
                <th style={{ textAlign: 'center', padding: '10px 12px' }}>Manager</th>
                <th style={{ textAlign: 'center', padding: '10px 12px' }}>Weight</th>
                <th style={{ textAlign: 'center', padding: '10px 12px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {selectedKpis.map(kpi => (
                <tr key={kpi.AssignmentId} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span>{kpi.KpiTitle}</span>
                      {kpi.IsRecurring && (
                        <span style={{
                          padding: '1px 7px', borderRadius: 99, fontSize: 10, fontWeight: 700,
                          background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0',
                          textTransform: 'uppercase',
                        }}>Recurring</span>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          recurringMutation.mutate({ kpiCode: kpi.KpiCode, is_recurring: !kpi.IsRecurring });
                        }}
                        disabled={recurringMutation.isPending}
                        title="Toggle monthly roll-forward"
                        style={{
                          padding: '2px 8px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                          background: kpi.IsRecurring ? '#ecfdf5' : '#f9fafb',
                          color: kpi.IsRecurring ? '#047857' : '#6b7280',
                          border: kpi.IsRecurring ? '1px solid #a7f3d0' : '1px solid #d1d5db',
                          fontWeight: 600,
                        }}
                      >{kpi.IsRecurring ? 'Recurring on' : 'Make recurring'}</button>
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>{kpi.KpiCode}</div>
                  </td>
                  <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                    <span style={{ 
                      background: '#f3f4f6', 
                      padding: '2px 8px', 
                      borderRadius: '4px', 
                      fontSize: '12px',
                      fontWeight: 500 
                    }}>
                      {kpi.PeriodName}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                    {kpi.HasSelfRating ? (
                      <span style={{ color: '#059669', fontWeight: 600 }}>{kpi.SelfScore?.toFixed(1) || '✓'}</span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>Pending</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                    {kpi.HasManagerRating ? (
                      <span style={{ color: '#059669', fontWeight: 600 }}>{kpi.ManagerScore?.toFixed(1) || '✓'}</span>
                    ) : (
                      <span style={{ 
                        color: kpi.HasSelfRating ? '#f59e0b' : '#9ca3af',
                        fontWeight: kpi.HasSelfRating ? 600 : 400
                      }}>
                        {kpi.HasSelfRating ? 'Review!' : 'Waiting'}
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                    {kpi.Weight?.toFixed(2)}
                  </td>
                  <td style={{ textAlign: 'center', padding: '10px 12px' }}>
                    <Link 
                      to={`/rating/${encodeURIComponent(kpi.EmployeeUpn)}/${kpi.KpiCode}/${kpi.PeriodId}`}
                      style={{
                        padding: '4px 12px', fontSize: 12, borderRadius: 4,
                        background: kpi.HasSelfRating && !kpi.HasManagerRating ? '#3b82f6' : '#6b7280',
                        color: '#fff', textDecoration: 'none', display: 'inline-block'
                      }}
                    >
                      {kpi.HasSelfRating && !kpi.HasManagerRating ? 'Review Now' : 'Open'}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ── Helper Components ── */

const StatCard: React.FC<{ label: string; value: React.ReactNode; icon: string; highlight?: boolean }> = 
  ({ label, value, icon, highlight }) => (
  <div style={{
    background: highlight ? '#fef3c7' : '#fff',
    border: highlight ? '2px solid #f59e0b' : '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '1rem',
    textAlign: 'center',
  }}>
    <div style={{ fontSize: '24px', marginBottom: '4px' }}>{icon}</div>
    <div style={{ fontSize: '24px', fontWeight: 700, color: highlight ? '#d97706' : '#374151' }}>{value}</div>
    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{label}</div>
  </div>
);

const TeamMemberCard: React.FC<{ 
  employee: EmployeeSummary; 
  isSelected: boolean;
  onSelect: () => void;
}> = ({ employee, isSelected, onSelect }) => {
  const { displayName, totalKpis, selfRated, managerRated, avgManagerScore, completionPct, needsAttention, fiscalYears, periodRange } = employee;
  
  const ringColor = completionPct >= 100 ? '#10b981' : completionPct >= 50 ? '#f59e0b' : '#ef4444';
  const r = 40;
  const circ = 2 * Math.PI * r;
  const offset = circ - (completionPct / 100) * circ;

  return (
    <div 
      onClick={onSelect}
      style={{
        background: isSelected ? '#eff6ff' : '#fff',
        border: isSelected ? '2px solid #3b82f6' : needsAttention ? '2px solid #f59e0b' : '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '1.25rem',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: isSelected ? '0 4px 12px rgba(59, 130, 246, 0.15)' : '0 1px 3px rgba(0,0,0,0.05)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        {/* Progress Ring */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <svg width="90" height="90" viewBox="0 0 90 90">
            <circle cx="45" cy="45" r={r} fill="none" stroke="#e5e7eb" strokeWidth="8" />
            <circle
              cx="45" cy="45" r={r}
              fill="none"
              stroke={ringColor}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circ}
              strokeDashoffset={offset}
              transform="rotate(-90 45 45)"
              style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
            />
          </svg>
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '16px', fontWeight: 700, color: ringColor,
          }}>
            {completionPct}%
          </div>
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ 
            fontSize: '15px', 
            fontWeight: 700, 
            color: '#374151',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginBottom: '4px'
          }}>
            {displayName}
          </div>
          
          {/* Fiscal Year Badge */}
          {fiscalYears.length > 0 && (
            <div style={{ marginBottom: '6px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {fiscalYears.map(fy => (
                <span key={fy} style={{
                  background: '#dbeafe',
                  color: '#1e40af',
                  fontSize: '10px',
                  fontWeight: 600,
                  padding: '2px 6px',
                  borderRadius: '4px'
                }}>{fy}</span>
              ))}
              {periodRange && (
                <span style={{
                  color: '#6b7280',
                  fontSize: '10px',
                  padding: '2px 0'
                }}>({periodRange})</span>
              )}
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '1rem', fontSize: '12px', color: '#6b7280' }}>
            <div>
              <span style={{ fontWeight: 600, color: '#374151' }}>{totalKpis}</span> KPIs
            </div>
            <div>
              Self: <span style={{ 
                fontWeight: 600, 
                color: selfRated === totalKpis ? '#059669' : '#f59e0b' 
              }}>{selfRated}/{totalKpis}</span>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '1rem', fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            <div>
              Manager: <span style={{ 
                fontWeight: 600, 
                color: managerRated === totalKpis ? '#059669' : '#f59e0b' 
              }}>{managerRated}/{totalKpis}</span>
            </div>
            {avgManagerScore !== null && (
              <div>
                Avg: <span style={{ fontWeight: 600, color: '#374151' }}>{avgManagerScore.toFixed(1)}</span>/5
              </div>
            )}
          </div>

          {needsAttention && (
            <div style={{ 
              marginTop: '8px', 
              fontSize: '11px', 
              color: '#d97706',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              ⚠️ Needs attention
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ProgressBar: React.FC<{ pct: number }> = ({ pct }) => {
  const color = pct >= 100 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div style={{ 
      width: '80px', 
      height: '8px', 
      background: '#e5e7eb', 
      borderRadius: '4px', 
      overflow: 'hidden',
      display: 'inline-block'
    }}>
      <div style={{
        width: `${Math.min(pct, 100)}%`,
        height: '100%',
        background: color,
        borderRadius: '4px',
        transition: 'width 0.3s ease-out'
      }} />
    </div>
  );
};

export default TeamAssignmentsPage;
